import { useCallback, useMemo } from 'react';
import { InlineNotification } from '@carbon/react';
import type { Row } from '@tanstack/react-table';
import { DataTable } from 'components/data-table';
import type { WellCompletionRow } from 'types/well-completions';
import type { PerforationRow } from 'types/well-perforations';
import { exportTableToXlsx } from 'utils/tables';
import { perforationsColumns } from './perforations-columns';
import {
  flattenPerforationIntervals,
  perforationIntervalExportColumns,
} from './perforations-export';
import { perforationGroupColumns } from './perforations-group-columns';
import {
  groupPerforationsByCompletion,
  type PerforationGroupRow,
  withCompletionDates,
} from './perforations-grouping';
import { useWellPerforations } from './use-well-perforations';

// One empty array for every render, so the grouped rows keep their identity without completions.
const NO_COMPLETIONS: WellCompletionRow[] = [];

interface PerforationsTableProps {
  /** The `resources.queries.wellPerforations` href from the wells resource graph. */
  resourceHref: string;
  /** The well's 10-digit uwi — the read is keyed by it. */
  uwi: string;
  /**
   * The well's completions, for each completion's date (the caller reads them with
   * `useWellCompletions` from `lib/wells`). Without them the column stays empty.
   */
  completions?: WellCompletionRow[];
  /** The failed completions read, or null: the rows still show, with a warning. */
  completionsError?: Error | null;
}

/**
 * The nested detail view for one completion's perforation intervals — reuses the
 * same 23 columns the flat table showed, pre-sorted by Top Depth MD ascending.
 * Follows the `SlotFormationTable` sub-table convention: a plain nested
 * `DataTable` with the interactive chrome (sort / filter / search / export) off,
 * since it's a fixed, pre-sorted detail view.
 *
 * @param {object} props - `intervals` — the completion's rows, already sorted.
 * @return {JSX.Element} The nested intervals table.
 */
const PerforationIntervalsSubTable = ({
  intervals,
}: {
  intervals: PerforationRow[];
}) => (
  <DataTable<PerforationRow>
    data={intervals}
    columns={perforationsColumns}
    getRowId={(row) => String(row.id)}
    config={{
      sorting: false,
      columnResizing: false,
      columnReordering: false,
      columnPinning: false,
      columnVisibility: false,
      globalSearch: false,
      columnFilters: false,
      export: false,
      hideFooter: true,
    }}
  />
);

/**
 * Renders a group row's expanded detail.
 *
 * @param {Row<PerforationGroupRow>} row - The expanded completion's table row.
 * @return {JSX.Element} That completion's intervals sub-table.
 */
const renderPerforationIntervals = (row: Row<PerforationGroupRow>) => (
  <PerforationIntervalsSubTable intervals={row.original.intervals} />
);

/**
 * The Well Page **Perforations** table (PRD Story 1.2, refined per Dustin/Eira's
 * UAT feedback). One row per completion (UWI14 + Completion #) showing its
 * interval count and depth range; expanding a row reveals that completion's full
 * interval detail, sorted by Top Depth MD ascending. Default sort is UWI14 then
 * Completion # ascending, and the global search bar filters the completion
 * summaries.
 *
 * Grouping, search, sort, and pagination all run client-side over the complete
 * per-well set that {@link useWellPerforations} assembles — the endpoint supports
 * none of them, and computing any of them per server page would split a
 * completion across a page boundary.
 *
 * @param {PerforationsTableProps} props - The well's uwi, its perforations query href and its completions.
 * @return {JSX.Element} The grouped perforations table.
 */
export const PerforationsTable = ({
  uwi,
  resourceHref,
  completions = NO_COMPLETIONS,
  completionsError = null,
}: PerforationsTableProps) => {
  const { data, loading, error } = useWellPerforations(uwi, resourceHref);

  // Grouped by completion (UWI14 + Completion #) — see perforations-grouping.ts —
  // with each completion's date from the completions read.
  const groups = useMemo(
    () => withCompletionDates(groupPerforationsByCompletion(data), completions),
    [data, completions]
  );

  // Exports the INTERVAL rows, not the completion summaries the outer table
  // shows. DataTable's built-in export walks the outer table, so it would write
  // five group columns and lose every interval — the actual perforation data.
  // Every interval goes out regardless of which groups happen to be expanded,
  // and regardless of the search filter, matching the Landing/Spacing exports.
  const handleExport = useCallback(() => {
    exportTableToXlsx({
      filename: 'perforations-export',
      sheetName: 'Perforations',
      columns: perforationIntervalExportColumns,
      data: flattenPerforationIntervals(groups),
    });
  }, [groups]);

  return (
    <>
      {completionsError && (
        <InlineNotification
          kind="warning"
          title="Completion dates could not be loaded."
          hideCloseButton
          lowContrast
        />
      )}
      <DataTable<PerforationGroupRow>
        data={groups}
        columns={perforationGroupColumns}
        // Composite (UWI14, Completion #) key — a UWI14 can carry several
        // completions, so keying on uwi14Completion alone collapses them.
        getRowId={(row) => row.groupKey}
        // The dates are secondary: the rows show as soon as the perforations load, and a failed
        // date read leaves the dates blank under the warning above.
        isLoading={loading}
        error={error}
        renderRowSubComponent={renderPerforationIntervals}
        scroll={{ mode: 'paginated' }}
        searchPlaceholder="Search perforations…"
        config={{
          sorting: true,
          columnResizing: true,
          columnReordering: true,
          columnPinning: true,
          columnVisibility: true,
          globalSearch: true,
          columnFilters: false,
          export: {
            exportPresets: [
              {
                name: 'Export',
                format: 'xlsx',
                filename: 'perforations-export',
                onExport: handleExport,
              },
            ],
          },
        }}
        initialTableState={{
          // UWI14 asc, tie-broken on Completion # asc by that column's sortingFn.
          sortColumn: 'uwi14Completion',
          sortDirection: 'asc',
        }}
      />
    </>
  );
};
